// Remembers where a visitor was headed when we interrupted them to sign in.
//
// Why this exists: a logged-out visitor who taps "Apply" on a job is sent to
// sign up, and used to land on /dashboard afterwards — the job they came for was
// gone. The old attempt passed `returnTo` through React Router's location.state,
// which never arrived: /auth redirects to /sign-up forwarding only location.search
// (App.js AuthRedirect), and every auth transition is a full window.location
// reload, which wipes router state anyway.
//
// So the destination is kept in storage instead, which survives the redirect
// chain, the hard reloads, and the email-confirmation round trip (the user leaves
// the browser and comes back to /auth/callback with no trace of where they were).
//
// Storage mirrors the auth session's approach: localStorage when it works, a
// cookie when it doesn't. Facebook/Messenger in-app browsers — where our shared
// job links are opened — block localStorage outright.

const KEY = 'bt_return_to';
const MAX_AGE_MS = 60 * 60 * 1000; // 1 hour: long enough to read email, short enough not to surprise
const MAX_LEN = 512;
const MAX_LABEL_LEN = 120; // a job title, kept short enough for a one-line card

// A human-readable label (the job title) shown on the "Continue your application"
// card. Display-only — React escapes it on render — but we still strip control
// characters and cap the length so a hostile/oversized title can't distort the UI.
function sanitizeLabel(value) {
  if (typeof value !== 'string') return null;
  let out = '';
  for (let i = 0; i < value.length && out.length < MAX_LABEL_LEN; i += 1) {
    const c = value.charCodeAt(i);
    // Drop control chars and DEL; collapse other whitespace to a single space.
    if (c < 0x20 || c === 0x7f) {
      if (out && out[out.length - 1] !== ' ') out += ' ';
    } else {
      out += value[i];
    }
  }
  out = out.trim();
  return out || null;
}

// Control characters and whitespace can smuggle a payload past naive checks.
// Hyphens must stay legal — every job slug is hyphenated.
function hasUnsafeChars(path) {
  for (let i = 0; i < path.length; i += 1) {
    const c = path.charCodeAt(i);
    // control chars, space, DEL, and backslash (browsers fold \ to /)
    if (c <= 0x20 || c === 0x7f || c === 0x5c) return true;
  }
  return false;
}

/**
 * Only ever return a same-origin relative path. Anything else is an open-redirect
 * vector: a crafted returnTo of https://evil.example would let an attacker bounce
 * a freshly-authenticated user off our domain onto a lookalike login page.
 * @returns {string|null}
 */
export function sanitizeReturnTo(value) {
  if (typeof value !== 'string') return null;
  const path = value.trim();
  if (!path || path.length > MAX_LEN) return null;
  // Must be a rooted relative path.
  if (!path.startsWith('/')) return null;
  // "//evil.com" and "/\evil.com" are protocol-relative — they leave our origin.
  if (path.startsWith('//') || path.startsWith('/\\')) return null;
  if (hasUnsafeChars(path)) return null;
  // Never bounce back into the auth funnel itself (redirect loops).
  if (/^\/(auth|sign-up|login|check-email)(\/|$|\?)/i.test(path)) return null;
  return path;
}

const cookie = {
  read() {
    const hit = (document.cookie || '').split('; ').find((c) => c.startsWith(`${KEY}=`));
    return hit ? decodeURIComponent(hit.slice(KEY.length + 1)) : null;
  },
  write(raw) {
    const secure = window.location.protocol === 'https:' ? ';Secure' : '';
    document.cookie = `${KEY}=${encodeURIComponent(raw)};path=/;max-age=${MAX_AGE_MS / 1000};SameSite=Lax${secure}`;
  },
  clear() {
    const secure = window.location.protocol === 'https:' ? ';Secure' : '';
    document.cookie = `${KEY}=;path=/;max-age=0;SameSite=Lax${secure}`;
  },
};

/**
 * Remember where to come back to. Call this right before sending someone to auth.
 * @param {string} path  same-origin relative path to return to
 * @param {string} [label]  optional human label (job title) for the dashboard card
 */
export function rememberReturnTo(path, label) {
  const clean = sanitizeReturnTo(path);
  if (!clean) return;
  const cleanLabel = sanitizeLabel(label);
  const raw = JSON.stringify({ path: clean, at: Date.now(), ...(cleanLabel ? { label: cleanLabel } : {}) });
  try {
    window.localStorage.setItem(KEY, raw);
  } catch {
    // localStorage blocked (in-app browser / private mode) — the cookie carries it.
  }
  try {
    cookie.write(raw);
  } catch {
    /* nothing else to fall back to */
  }
}

/**
 * Read and clear the stored destination. Single-use on purpose: a leftover value
 * must not hijack an unrelated login days later.
 * @returns {string|null}
 */
export function takeReturnTo() {
  let raw = null;
  try {
    raw = window.localStorage.getItem(KEY);
  } catch {
    /* fall through to cookie */
  }
  if (!raw) {
    try {
      raw = cookie.read();
    } catch {
      /* no storage at all */
    }
  }
  clearReturnTo();
  if (!raw) return null;
  try {
    const { path, at } = JSON.parse(raw);
    if (!at || Date.now() - at > MAX_AGE_MS) return null;
    return sanitizeReturnTo(path); // re-validate: storage is user-writable
  } catch {
    return null;
  }
}

/** Read the raw stored blob (localStorage first, cookie fallback). Does NOT clear. */
function readRaw() {
  let raw = null;
  try {
    raw = window.localStorage.getItem(KEY);
  } catch {
    /* fall through to cookie */
  }
  if (!raw) {
    try {
      raw = cookie.read();
    } catch {
      /* no storage at all */
    }
  }
  return raw;
}

/**
 * Look at the stored destination WITHOUT consuming it. Used by the dashboard's
 * "Continue your application" card, which must be able to render the remembered
 * job repeatedly and only clear it when the visitor actually clicks through.
 * Applies the same TTL + re-validation as takeReturnTo.
 * @returns {string|null}
 */
export function peekReturnTo() {
  const raw = readRaw();
  if (!raw) return null;
  try {
    const { path, at } = JSON.parse(raw);
    if (!at || Date.now() - at > MAX_AGE_MS) return null;
    return sanitizeReturnTo(path); // re-validate: storage is user-writable
  } catch {
    return null;
  }
}

/**
 * The human label (job title) stored alongside the destination, if any and if
 * the intent is still valid. Never throws; returns null when absent/expired.
 * @returns {string|null}
 */
export function peekReturnToLabel() {
  const raw = readRaw();
  if (!raw) return null;
  try {
    const { label, at } = JSON.parse(raw);
    if (!at || Date.now() - at > MAX_AGE_MS) return null;
    return sanitizeLabel(label);
  } catch {
    return null;
  }
}

export function clearReturnTo() {
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
  try {
    cookie.clear();
  } catch {
    /* ignore */
  }
}
